<template>
  <div>
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
      <p class="ck-subtitulo mb-0" style="max-width: 780px">
        Cada jornada é uma <b>escala</b>: o horário de cada dia, com folgas. Use <b>semanas alternadas</b> para
        "sábado sim, sábado não" e <b>revezamento em dias</b> para 12x36 e similares. Mudanças pontuais
        (um dia em outro horário, troca de folga) são feitas em <router-link to="/escalas">Escalas e exceções</router-link>.
      </p>
      <button class="btn btn-info" @click="editar()"><i class="bi bi-plus-lg"></i> Nova jornada</button>
    </div>

    <div class="card mb-3">
      <div class="card-body py-2">
        <div class="input-group">
          <span class="input-group-text bg-white border-0"><i class="bi bi-search"></i></span>
          <input v-model.trim="busca" class="form-control border-0" placeholder="Pesquisar jornada pelo nome ou código">
        </div>
      </div>
    </div>

    <div v-if="carregando" class="text-center py-5"><span class="spinner-border"></span></div>
    <div v-else-if="!filtradas.length" class="card">
      <div class="card-body text-center py-5">
        <i class="bi bi-clock-history" style="font-size: 2.4rem; color: var(--ck-primaria)"></i>
        <h5 class="mt-2">{{ busca ? 'Nenhuma jornada encontrada' : 'Nenhuma jornada cadastrada' }}</h5>
        <button v-if="!busca" class="btn btn-info mt-2" @click="editar()">Cadastrar a primeira jornada</button>
      </div>
    </div>

    <div class="grade-jornadas">
      <div v-for="j in filtradas" :key="j.id" class="card jornada">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start gap-2">
            <div>
              <h5 class="mb-0">{{ j.nome }}</h5>
              <small class="text-muted">Código {{ j.id }} · média semanal {{ j.media_semanal }}</small>
            </div>
            <span :class="['badge', j.tipo === 1 ? 'text-bg-warning' : (j.ciclo_dias > 7 ? 'text-bg-info' : 'badge-suave')]">{{ j.escala }}</span>
          </div>

          <!-- Miniatura do ciclo -->
          <div v-if="j.tipo === 0" class="mini">
            <div v-for="s in j.ciclo_dias / 7" :key="s" class="mini-semana">
              <span v-if="j.ciclo_dias > 7" class="mini-rot">S{{ s }}</span>
              <span v-for="d in ORDEM" :key="d" :class="['mini-dia', { folga: j.dias[(s - 1) * 7 + d].folga }]"
                    :title="NOMES[d] + ': ' + (j.dias[(s - 1) * 7 + d].folga ? 'folga' : faixa(j.dias[(s - 1) * 7 + d]))">
                {{ ABREV[d][0] }}
              </span>
            </div>
          </div>
          <div v-else class="mini">
            <div class="mini-semana flex-wrap">
              <span v-for="(d, i) in j.dias" :key="i" :class="['mini-dia', { folga: d.folga }]"
                    :title="'Dia ' + (i + 1) + ': ' + (d.folga ? 'folga' : faixa(d))">{{ i + 1 }}</span>
            </div>
          </div>
          <p class="descricao">{{ j.descricao }}</p>
          <p v-if="!j.segue_feriados" class="descricao"><i class="bi bi-calendar-x"></i> Não segue feriados</p>
        </div>
        <div class="card-footer d-flex gap-2">
          <button class="btn btn-sm btn-info flex-fill" @click="editar(j)"><i class="bi bi-pencil"></i> Editar</button>
          <button class="btn btn-sm btn-light" @click="editar(j, true)" title="Duplicar"><i class="bi bi-copy"></i></button>
          <button class="btn btn-sm btn-light text-danger" @click="excluir(j)" title="Excluir"><i class="bi bi-trash"></i></button>
        </div>
      </div>
    </div>

    <!-- Editor da jornada -->
    <div class="modal fade" ref="modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ form.id ? 'Editar jornada' : 'Nova jornada' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3 align-items-end">
              <div class="col-lg-5">
                <label>Nome da jornada</label>
                <input v-model.trim="form.nome" class="form-control" placeholder="Ex.: Comercial com sábado alternado" ref="nome">
              </div>
              <div class="col-lg-4">
                <label>Tipo de escala</label>
                <div class="btn-group w-100" role="group">
                  <button type="button" :class="['btn', form.tipo === 0 ? 'btn-info' : 'btn-light']" @click="mudarTipo(0)">
                    <i class="bi bi-calendar-week"></i> Semanal
                  </button>
                  <button type="button" :class="['btn', form.tipo === 1 ? 'btn-info' : 'btn-light']" @click="mudarTipo(1)">
                    <i class="bi bi-arrow-repeat"></i> Revezamento
                  </button>
                </div>
              </div>
              <div class="col-lg-3" v-if="form.tipo === 0">
                <label>Semanas no ciclo</label>
                <select class="form-select" :value="form.ciclo_dias / 7" @change="redimensionar($event.target.value * 7)">
                  <option :value="1">1 — toda semana igual</option>
                  <option :value="2">2 — ex.: sábado sim, sábado não</option>
                  <option :value="3">3 semanas</option>
                  <option :value="4">4 semanas</option>
                </select>
              </div>
              <div class="col-lg-3" v-else>
                <label>Dias no ciclo</label>
                <input type="number" min="2" max="60" class="form-control" :value="form.ciclo_dias"
                       @change="redimensionar(Math.min(60, Math.max(2, parseInt($event.target.value) || 2)))">
              </div>
            </div>

            <div class="form-check form-switch mt-3">
              <input class="form-check-input" type="checkbox" id="segueferiados" v-model="form.segue_feriados">
              <label class="form-check-label m-0" for="segueferiados">
                Segue o cadastro de feriados
                <small class="text-muted fw-normal">— desligue em escalas como 12x36, em que o plantão cai normalmente no feriado</small>
              </label>
            </div>

            <div class="modelos mt-3">
              <span class="ck-subtitulo me-1">Modelos:</span>
              <button v-for="m in MODELOS" :key="m.nome" class="btn btn-sm btn-light" @click="aplicarModelo(m)">{{ m.nome }}</button>
            </div>

            <div class="padrao mt-3">
              <div class="row g-2 align-items-end">
                <div class="col-12 col-md-auto"><strong class="small">Horário padrão</strong></div>
                <div class="col-6 col-md-2"><label>Entrada</label><input type="time" v-model="padrao.hora_entrada" class="form-control"></div>
                <div class="col-6 col-md-2"><label>Início intervalo</label><input type="time" v-model="padrao.hora_inicio_intervalo" class="form-control"></div>
                <div class="col-6 col-md-2"><label>Fim intervalo</label><input type="time" v-model="padrao.hora_fim_intervalo" class="form-control"></div>
                <div class="col-6 col-md-2"><label>Saída</label><input type="time" v-model="padrao.hora_saida" class="form-control"></div>
                <div class="col-12 col-md">
                  <button class="btn btn-light w-100" @click="aplicarPadrao"><i class="bi bi-arrow-down-square"></i> Aplicar aos dias de trabalho</button>
                </div>
              </div>
            </div>

            <!-- Dias do ciclo -->
            <div v-for="(bloco, b) in blocos" :key="b" class="mt-3">
              <div v-if="bloco.titulo" class="bloco-titulo">
                {{ bloco.titulo }}
                <small v-if="form.tipo === 0 && blocos.length > 1" class="text-muted fw-normal">
                  — a semana do dia-base do colaborador é a semana 1
                </small>
              </div>
              <div class="table-responsive">
                <table class="table table-sm align-middle mb-0 dias">
                  <thead>
                    <tr><th style="width: 130px">Dia</th><th style="width: 90px">Trabalha</th><th>Entrada</th><th>Início int.</th><th>Fim int.</th><th>Saída</th><th class="text-end" style="width: 80px">Horas</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in bloco.posicoes" :key="p" :class="{ folga: form.dias[p].folga }">
                      <td class="fw-semibold">{{ rotulo(p) }}</td>
                      <td>
                        <div class="form-check form-switch m-0">
                          <input class="form-check-input" type="checkbox" :checked="!form.dias[p].folga" @change="alternar(p, $event.target.checked)">
                        </div>
                      </td>
                      <template v-if="!form.dias[p].folga">
                        <td><input type="time" v-model="form.dias[p].hora_entrada" class="form-control form-control-sm"></td>
                        <td><input type="time" v-model="form.dias[p].hora_inicio_intervalo" class="form-control form-control-sm"></td>
                        <td><input type="time" v-model="form.dias[p].hora_fim_intervalo" class="form-control form-control-sm"></td>
                        <td><input type="time" v-model="form.dias[p].hora_saida" class="form-control form-control-sm"></td>
                        <td class="text-end num">{{ hhmm(minutosDia(form.dias[p])) }}</td>
                      </template>
                      <td v-else colspan="5" class="text-muted small">Folga / DSR</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer justify-content-between">
            <div class="small text-muted">
              Média semanal prevista: <b class="text-body">{{ hhmm(mediaSemanal) }}</b>
              <span v-if="temNoturno"> · horas noturnas já com a redução legal (52min30s)</span>
            </div>
            <div>
              <button class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
              <button class="btn btn-info ms-2" @click="salvar" :disabled="salvando">
                <span v-if="salvando" class="spinner-border spinner-border-sm"></span> Salvar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
//Eu sou o caminho, a verdade e a vida. Ninguém vem ao Pai senão por mim. (João 14:6);
import Swal from 'sweetalert2'
import { Modal } from 'bootstrap'
import util from '@/util'

const NOMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
const ABREV = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const ORDEM = [1, 2, 3, 4, 5, 6, 0] // segunda a domingo

const folga = () => ({ folga: 1, hora_entrada: '', hora_inicio_intervalo: '', hora_fim_intervalo: '', hora_saida: '' })
const dia = (e, s, ii = '', fi = '') => ({ folga: 0, hora_entrada: e, hora_inicio_intervalo: ii, hora_fim_intervalo: fi, hora_saida: s })
const COMERCIAL = () => dia('08:00', '17:00', '12:00', '13:00')
const SABADO = () => dia('08:00', '12:00')

/** Semana (domingo a sábado) a partir de uma função por dia da semana. */
const semana = (f) => [0, 1, 2, 3, 4, 5, 6].map(f)

const MODELOS = [
  { nome: 'Seg a Sex', tipo: 0, dias: () => semana(d => (d >= 1 && d <= 5 ? COMERCIAL() : folga())) },
  { nome: 'Seg a Sex + sábado manhã', tipo: 0, dias: () => semana(d => (d >= 1 && d <= 5 ? COMERCIAL() : d === 6 ? SABADO() : folga())) },
  { nome: 'Sábado sim, sábado não', tipo: 0, dias: () => [
      ...semana(d => (d >= 1 && d <= 5 ? COMERCIAL() : d === 6 ? SABADO() : folga())),
      ...semana(d => (d >= 1 && d <= 5 ? COMERCIAL() : folga())),
    ] },
  { nome: '12x36 diurno', tipo: 1, dias: () => [dia('07:00', '19:00', '12:00', '13:00'), folga()] },
  { nome: '12x36 noturno', tipo: 1, dias: () => [dia('19:00', '07:00', '00:00', '01:00'), folga()] },
  { nome: '6x1', tipo: 1, segue: true, dias: () => [COMERCIAL(), COMERCIAL(), COMERCIAL(), COMERCIAL(), COMERCIAL(), COMERCIAL(), folga()] },
]

function minutos(t) { const [h, m] = String(t).split(':').map(Number); return h * 60 + m }

/** Minutos de [ini, fim) com a hora noturna (22h às 5h) reduzida, igual ao servidor. */
function comReducao(ini, fim) {
  let diurno = 0, noturno = 0
  for (let t = ini; t < fim; t++) {
    const m = t % 1440
    if (m < 300 || m >= 1320) noturno++; else diurno++
  }
  return { total: diurno + noturno * 60 / 52.5, noturno }
}

export default {
  name: 'jornadas',
  data() {
    return {
      NOMES, ABREV, ORDEM, MODELOS,
      lista: [],
      busca: '',
      carregando: false,
      salvando: false,
      modal: null,
      form: this.novoForm(),
      padrao: { hora_entrada: '08:00', hora_inicio_intervalo: '12:00', hora_fim_intervalo: '13:00', hora_saida: '17:00' },
    }
  },
  computed: {
    filtradas() {
      const b = this.busca.toLowerCase()
      return b ? this.lista.filter(j => String(j.id) === b || j.nome.toLowerCase().includes(b)) : this.lista
    },
    blocos() {
      const f = this.form
      if (f.tipo === 1) return [{ titulo: '', posicoes: f.dias.map((_, i) => i) }]
      const out = []
      for (let s = 0; s < f.ciclo_dias / 7; s++) {
        out.push({ titulo: f.ciclo_dias > 7 ? 'Semana ' + (s + 1) : '', posicoes: ORDEM.map(d => s * 7 + d) })
      }
      return out
    },
    mediaSemanal() {
      const tot = this.form.dias.reduce((a, d) => a + (d.folga ? 0 : this.minutosDia(d)), 0)
      return tot * 7 / Math.max(1, this.form.ciclo_dias)
    },
    temNoturno() {
      return this.form.dias.some(d => !d.folga && d.hora_entrada && d.hora_saida &&
        (minutos(d.hora_saida) <= minutos(d.hora_entrada) || minutos(d.hora_saida) > 1320 || minutos(d.hora_entrada) < 300))
    },
  },
  mounted() {
    this.carregar()
  },
  methods: {
    novoForm() {
      return { id: 0, nome: '', tipo: 0, ciclo_dias: 7, segue_feriados: true, dias: MODELOS[0].dias() }
    },
    hhmm(min) {
      min = Math.round(min || 0)
      return String(Math.floor(min / 60)).padStart(2, '0') + ':' + String(min % 60).padStart(2, '0')
    },
    faixa(d) {
      return d.hora_entrada + (d.hora_inicio_intervalo ? ' - ' + d.hora_inicio_intervalo + ' / ' + d.hora_fim_intervalo : '') + ' - ' + d.hora_saida
    },
    rotulo(p) {
      return this.form.tipo === 1 ? 'Dia ' + (p + 1) : NOMES[p % 7]
    },
    minutosDia(d) {
      if (!d.hora_entrada || !d.hora_saida) return 0
      const e1 = minutos(d.hora_entrada)
      let s2 = minutos(d.hora_saida); if (s2 <= e1) s2 += 1440
      if (d.hora_inicio_intervalo && d.hora_fim_intervalo && d.hora_inicio_intervalo !== d.hora_fim_intervalo) {
        let s1 = minutos(d.hora_inicio_intervalo); if (s1 < e1) s1 += 1440
        let e2 = minutos(d.hora_fim_intervalo); if (e2 < s1) e2 += 1440
        return comReducao(e1, s1).total + comReducao(e2, s2).total
      }
      return comReducao(e1, s2).total
    },
    req(op, extra = []) {
      return util.reqaxios([{ parametro: 'op', valor: op }, { parametro: 'idempresa', valor: this.$store.state.usuario.idempresa }, ...extra],
                           'post', util.servidor + '/controller/jornadas.php')
    },
    async carregar() {
      this.carregando = true
      try {
        const r = await this.req('listagem', [{ parametro: 'limit', valor: 1000 }, { parametro: 'offset', valor: 0 }])
        this.lista = Array.isArray(r.data) ? r.data : []
        if (!Array.isArray(r.data)) util.respostaOk(r.data)
      } finally {
        this.carregando = false
      }
    },
    editar(j, duplicar = false) {
      this.form = j
        ? { id: duplicar ? 0 : j.id, nome: duplicar ? j.nome + ' (cópia)' : j.nome, tipo: j.tipo, ciclo_dias: j.ciclo_dias, segue_feriados: !!j.segue_feriados,
            dias: j.dias.map(d => ({ folga: d.folga, hora_entrada: d.hora_entrada, hora_inicio_intervalo: d.hora_inicio_intervalo,
                                     hora_fim_intervalo: d.hora_fim_intervalo, hora_saida: d.hora_saida })) }
        : this.novoForm()
      const prim = this.form.dias.find(d => !d.folga)
      if (prim) this.padrao = { ...prim }
      if (!this.modal) this.modal = new Modal(this.$refs.modal)
      this.modal.show()
      setTimeout(() => this.$refs.nome && this.$refs.nome.focus(), 300)
    },
    mudarTipo(t) {
      if (this.form.tipo === t) return
      this.form.tipo = t
      this.form.segue_feriados = t === 0
      if (t === 1) {
        this.form.ciclo_dias = 2
        this.form.dias = [dia(this.padrao.hora_entrada, this.padrao.hora_saida, this.padrao.hora_inicio_intervalo, this.padrao.hora_fim_intervalo), folga()]
      } else {
        this.form.ciclo_dias = 7
        this.form.dias = MODELOS[0].dias()
      }
    },
    /** Muda o tamanho do ciclo: novas semanas copiam a semana 1; novos dias entram como folga. */
    redimensionar(n) {
      n = parseInt(n)
      const d = this.form.dias
      if (n < d.length) this.form.dias = d.slice(0, n)
      else for (let i = d.length; i < n; i++) d.push(this.form.tipo === 0 ? { ...d[i % 7] } : folga())
      this.form.ciclo_dias = n
    },
    aplicarModelo(m) {
      this.form.tipo = m.tipo
      this.form.segue_feriados = m.segue !== undefined ? m.segue : m.tipo === 0
      this.form.dias = m.dias()
      this.form.ciclo_dias = this.form.dias.length
      if (!this.form.nome) this.form.nome = m.nome
    },
    aplicarPadrao() {
      if (!this.padrao.hora_entrada || !this.padrao.hora_saida) return util.notif('error', 'Atenção', 'Informe entrada e saída do horário padrão.')
      this.form.dias.forEach(d => { if (!d.folga) Object.assign(d, { ...this.padrao, folga: 0 }) })
    },
    alternar(p, trabalha) {
      const d = this.form.dias[p]
      if (trabalha) Object.assign(d, { ...this.padrao, folga: 0 })
      else Object.assign(d, folga())
    },
    async salvar() {
      if (!this.form.nome) return util.notif('error', 'Atenção', 'Informe o nome da jornada.')
      if (!this.form.dias.some(d => !d.folga)) return util.notif('error', 'Atenção', 'Marque ao menos um dia de trabalho.')
      this.salvando = true
      try {
        const r = await this.req('rotinacrud', [
          { parametro: 'opcrud', valor: this.form.id ? 2 : 1 },
          { parametro: 'registro', valor: JSON.stringify(this.form) },
        ])
        if (util.respostaOk(r.data)) {
          this.modal.hide()
          util.notif('success', 'Jornada salva', '')
          this.carregar()
        }
      } finally {
        this.salvando = false
      }
    },
    async excluir(j) {
      const ok = await Swal.fire({ title: `Excluir "${j.nome}"?`, text: 'Jornadas usadas por colaboradores (atuais ou no histórico) não podem ser excluídas.',
                                   icon: 'warning', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar' })
      if (!ok.isConfirmed) return
      const r = await this.req('rotinacrud', [{ parametro: 'opcrud', valor: 3 }, { parametro: 'registro', valor: JSON.stringify({ id: j.id }) }])
      if (util.respostaOk(r.data)) this.carregar()
    },
  },
}
</script>

<style scoped lang="scss">
.grade-jornadas { display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)); gap: 14px; }
.jornada h5 { font-size: 1.02rem; font-weight: 700; }
.mini { margin: 14px 0 8px; display: flex; flex-direction: column; gap: 5px; }
.mini-semana { display: flex; gap: 4px; align-items: center; }
.mini-rot { font-size: .68rem; font-weight: 700; color: var(--ck-texto-2); width: 20px; }
.mini-dia {
  width: 30px; height: 26px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center;
  font-size: .7rem; font-weight: 700; background: var(--ck-primaria-suave); color: var(--ck-primaria);
}
.mini-dia.folga { background: #f1f3f2; color: #a7b3af; }
.descricao { font-size: .8rem; color: var(--ck-texto-2); margin: 6px 0 0; }
.modelos { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.padrao { background: #f7faf9; border: 1px solid var(--ck-borda); border-radius: 12px; padding: 10px 12px; }
.padrao .form-group, .padrao label { margin-bottom: .15rem; }
.bloco-titulo { font-weight: 700; font-size: .85rem; margin-bottom: 4px; color: var(--ck-primaria); }
.dias th { font-size: .7rem; text-transform: uppercase; color: var(--ck-texto-2); letter-spacing: .04em; background: #f7faf9; }
.dias .form-control-sm { min-height: 32px; }
.dias tr.folga td { background: #fafbfb; }
.num { font-variant-numeric: tabular-nums; }
</style>
